/**
 * RF-13 · Calendario de acreditación (`/api/eventos-calendario`).
 *
 * Eventos con recordatorios configurables. Las reglas de alerta A1/A6 leen estos
 * eventos para avisar de vencimientos próximos y de fechas pasadas sin gestionar.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

// RF-13 · Calendario de acreditación.
// Lectura: calendario.ver (filtrada por visibilidad) · Gestión: calendario.gestionar.

export type EstadoEvento = 'programado' | 'confirmado' | 'cumplido' | 'aplazado' | 'cancelado'

/** Forma con la que el API de calendario devuelve este dato. */
export interface EventoCalendario {
  id_evento: number
  tipo_evento: string
  tipo_label: string
  titulo: string
  descripcion: string | null
  id_area: number | null
  id_campus: number | null
  id_ejecucion: number | null
  fecha_inicio: string
  fecha_fin: string | null
  recordatorios: number[] | null
  recordatorios_efectivos: number[]
  destinatarios: { roles?: string[]; usuarios?: number[] } | null
  estado: EstadoEvento
  historial_fechas: Array<{ de: string; a: string; en: string }> | null
  dias_restantes: number
  area?: { id_programa: number; nombre: string } | null
  campus?: { id_campus: number; nombre: string } | null
  creador?: { id: number; name: string } | null
}

/** Forma con la que el API de calendario devuelve este dato. */
export interface TipoEvento {
  clave: string
  label: string
  recordatorios_sugeridos: number[]
}

/** Forma con la que el API de calendario devuelve este dato. */
export interface EventoEditable {
  tipo_evento?: string
  titulo?: string
  descripcion?: string | null
  id_area?: number | null
  id_campus?: number | null
  id_ejecucion?: number | null
  fecha_inicio?: string
  fecha_fin?: string | null
  recordatorios?: number[]
  destinatarios?: { roles?: string[]; usuarios?: number[] }
  estado?: EstadoEvento
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

function q(params: Record<string, string | number | undefined>): string {
  const s = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&')
  return s ? `?${s}` : ''
}

/**
 * Consulta calendario · `GET /api/calendario`.
 *
 * @returns Resultado<EventoCalendario[]>
 */
export async function getEventosService(filtros: { desde?: string; hasta?: string; tipo?: string; id_area?: number } = {}): Promise<Resultado<EventoCalendario[]>> {
  const r = await http.get<EventoCalendario[]>(`/api/calendario${q(filtros)}`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el calendario') }
}

/**
 * Consulta calendario · `GET /api/calendario/proximos?dias=`.
 *
 * @returns Resultado<EventoCalendario[]>
 */
export async function getProximosEventosService(dias = 90): Promise<Resultado<EventoCalendario[]>> {
  const r = await http.get<EventoCalendario[]>(`/api/calendario/proximos?dias=${dias}`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudieron cargar los próximos eventos') }
}

/**
 * Consulta calendario · `GET /api/calendario/tipos`.
 *
 * @returns Resultado<TipoEvento[]>
 */
export async function getTiposEventoService(): Promise<Resultado<TipoEvento[]>> {
  const r = await http.get<TipoEvento[]>('/api/calendario/tipos')
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el catálogo de tipos') }
}

/**
 * Crea calendario · `POST /api/calendario`.
 *
 * @returns Resultado<EventoCalendario>
 */
export async function crearEventoService(datos: EventoEditable): Promise<Resultado<EventoCalendario>> {
  const r = await http.post<EventoCalendario>('/api/calendario', datos)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo registrar el evento') }
}

/**
 * Actualiza calendario · `PATCH /api/calendario`.
 *
 * @returns Resultado<EventoCalendario>
 */
export async function actualizarEventoService(id: number, datos: EventoEditable): Promise<Resultado<EventoCalendario>> {
  const r = await http.patch<EventoCalendario>(`/api/calendario/${id}`, datos)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo actualizar el evento') }
}

/** Confirmar / cumplir / cancelar / aplazar (aplazar requiere fecha nueva). */
export async function cambiarEstadoEventoService(id: number, estado: EstadoEvento, fechaNueva?: string): Promise<Resultado<EventoCalendario>> {
  const r = await http.patch<EventoCalendario>(`/api/calendario/${id}/estado`, {
    estado,
    ...(fechaNueva ? { fecha_inicio: fechaNueva } : {})
  })
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cambiar el estado del evento') }
}
